import { useState, useContext, useEffect } from "react";
import { Box, Typography, Button, Tooltip, Chip } from "@mui/material";
import {
  EllipsisVertical,
  Plus,
  Clipboard,
  Pencil,
  Sparkles,
} from "lucide-react";
import { TaskInspiration, Task } from "protogen/tasks_pb";
import { AgentPrompt } from "protogen/agents_service_pb";
import {
  useListAllMemberTaskInspirations,
  useListAllMemberTaskInspirationCategories,
} from "services/tasks";
import { Family } from "protogen/advisors_service_pb";
import { useNavigate } from "react-router-dom";
import useIsMobile from "components/hooks/useIsMobile";
import FlexBox from "components/common/FlexBox";
import { tasksPageTaskInspirationSubtitle } from "common/memberModuleResolver";
import { CurrentUserContext } from "components/context/RequireAuth";
import { planFlagsFromStatus } from "utils/memberFamilyPlan";
import useDigitalSubscriptionUX from "components/hooks/useDigitalSubscriptionUX";

export const InspiredTaskList = ({
  title,
  tasks,
  onTaskClick,
  numVisibileTasks = 3,
  showStatus = false,
  style = "contained",
  iconType = "Add",
  titleSx,
  onCategoryClick,
  onCopySuccess,
  playbooks,
}: {
  title: string;
  tasks: TaskInspiration[];
  onTaskClick: (task: TaskInspiration) => void;
  numVisibileTasks?: number;
  showStatus?: boolean;
  style?: "contained" | "normal";
  iconType?: "Add" | "More";
  titleSx?: React.CSSProperties;
  onCategoryClick?: (categoryRef: string) => void;
  onCopySuccess?: (task: TaskInspiration) => void;
  playbooks?: AgentPrompt[];
}) => {
  const [visibleTasks, setVisibleTasks] = useState<TaskInspiration[]>(
    tasks.slice(0, numVisibileTasks),
  );
  const isMobile = useIsMobile();

  const onCopy = (task: TaskInspiration) => {
    const prodOrigin = "https://app.findfaye.com";
    const currentOrigin = window.location.origin;
    const isLocal = currentOrigin.includes("localhost");
    const origin = isLocal ? currentOrigin : prodOrigin;
    const url = `${origin}/tasks/add/inspired/${encodeURIComponent(task.ref)}`;
    navigator.clipboard.writeText(url);
    onCopySuccess && onCopySuccess(task);
  };

  useEffect(() => {
    setVisibleTasks(tasks.slice(0, numVisibileTasks));
  }, [tasks]);
  return (
    <FlexBox direction="column" gap="8px">
      <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
        <Typography variant="bodyHeavy" color="#3D3D3D" sx={{ ...titleSx }}>
          {title}
        </Typography>
        {onCategoryClick && (
          <Button
            sx={{ height: "16px" }}
            size="small"
            variant="text"
            onClick={() => {
              onCategoryClick && onCategoryClick(tasks[0].categoryRef);
            }}
          >
            <Pencil size={16}></Pencil>
          </Button>
        )}
      </Box>
      <FlexBox direction="column" gap="8px">
        {visibleTasks.map((task, i) => {
          return (
            <FlexBox
              key={i}
              direction={style == "contained" ? "row" : "row-reverse"}
              variant={style == "contained" ? "contained" : "default"}
              justifyContent={style == "contained" ? "space-between" : "start"}
              alignItems={style == "contained" ? "center" : "start"}
              gap="8px"
              sx={{ paddingRight: isMobile ? "" : "16px" }}
            >
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                <Box sx={{ display: "flex", alignItems: "start", gap: "8px" }}>
                  <Typography variant="body" color="#3D3D3D">
                    {task.title}
                  </Typography>
                  {task.status === "active" && (
                    <Tooltip
                      title="Copy quick add link to clipboard"
                      placement="top"
                    >
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => {
                          onCopy(task);
                        }}
                        sx={{
                          height: "16px",
                          minWidth: "16px",
                          cursor: "pointer",
                          marginTop: "2px",
                        }}
                      >
                        <Clipboard size={16} color="#198282" />
                      </Button>
                    </Tooltip>
                  )}
                </Box>
                {showStatus && (
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: "8px" }}
                  >
                    <Typography
                      variant="body"
                      color={
                        task.status === "active"
                          ? "primary.main"
                          : "secondary.dark"
                      }
                    >
                      {task.status}
                    </Typography>
                    {task.playbookRef && playbooks && (
                      <Chip
                        icon={<Sparkles size={16} color="#198282" />}
                        size="small"
                        label={
                          playbooks.find((p) => p.ref === task.playbookRef)
                            ?.name || "Playbook"
                        }
                        sx={{
                          height: "20px",
                          fontSize: "12px",
                          backgroundColor: "#E8F4F4",
                          color: "#198282",
                        }}
                      />
                    )}
                  </Box>
                )}
              </Box>

              <Box
                onClick={() => {
                  onTaskClick(task);
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                {iconType === "Add" ? (
                  <Plus size={24} color="#198282" />
                ) : (
                  <EllipsisVertical size={24} color="#198282" />
                )}
              </Box>
            </FlexBox>
          );
        })}
      </FlexBox>
      {tasks.length > numVisibileTasks &&
        tasks.length !== visibleTasks.length && (
          <Button
            onClick={() => {
              setVisibleTasks(tasks);
            }}
            sx={{ height: "20px" }}
            variant="text"
          >
            See more
          </Button>
        )}
    </FlexBox>
  );
};
export default ({
  family,
  activeTasks,
  layout = "vertical",
}: {
  family: Family;
  activeTasks: Task[];
  layout?: "vertical" | "horizontal";
}) => {
  const navigate = useNavigate();
  const inspiredTasksRefs = activeTasks
    .filter((task) => {
      return task.sourceEntityType === "task_inspiration";
    })
    .map((task) => task.sourceEntityRef);
  const { data, request: listAllTaskInspirationRequest } =
    useListAllMemberTaskInspirations(() => {});
  const {
    data: inspirationCategories,
    request: listAllTaskInspirationCategoriesRequest,
  } = useListAllMemberTaskInspirationCategories(() => {});
  useEffect(() => {
    listAllTaskInspirationCategoriesRequest();
    listAllTaskInspirationRequest();
  }, []);

  const commonTasks = data?.taskInspirations
    .filter((task) => {
      const category = inspirationCategories?.taskInspirationCategories.find(
        (cat) => cat.ref === task.categoryRef,
      );
      return (
        category && category.title === "Popular" && task.status === "active"
      );
    })
    .sort((a, b) => {
      const aIncluded = inspiredTasksRefs.includes(a.ref);
      const bIncluded = inspiredTasksRefs.includes(b.ref);
      if (aIncluded && !bIncluded) return 1;
      if (!aIncluded && bIncluded) return -1;
      return 0;
    });

  const trendingTasks = data?.taskInspirations
    .filter((task) => {
      const category = inspirationCategories?.taskInspirationCategories.find(
        (cat) => cat.ref === task.categoryRef,
      );
      return (
        category && category.title === "Seasonal" && task.status === "active"
      );
    })
    .sort((a, b) => {
      const aIncluded = inspiredTasksRefs.includes(a.ref);
      const bIncluded = inspiredTasksRefs.includes(b.ref);
      if (aIncluded && !bIncluded) return 1;
      if (!aIncluded && bIncluded) return -1;
      return 0;
    });

  const onTaskClick = (task: TaskInspiration) => {
    navigate("/tasks/add", {
      state: { inspiredTask: task, family: family },
    });
  };

  const isMobile = useIsMobile();
  const currentUser = useContext(CurrentUserContext);
  const { isUnpaidSignup, familyStatus } = planFlagsFromStatus(
    currentUser.familyStatus,
  );
  const isDigitalSubscription = useDigitalSubscriptionUX();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <Typography variant={isMobile ? "h3" : "h4"}>
          Task inspiration ✨
        </Typography>
        <Typography variant="body" color="#3D3D3D">
          {tasksPageTaskInspirationSubtitle({
            isDigitalSubscription,
            isUnpaidSignup,
            familyStatus,
          })}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          flexDirection: layout === "vertical" ? "column" : "row",
          gap: layout === "vertical" ? "16px" : "48px",
        }}
      >
        <InspiredTaskList
          title="Seasonal tasks"
          tasks={trendingTasks || []}
          onTaskClick={onTaskClick}
        />
        <InspiredTaskList
          title="Popular tasks"
          tasks={commonTasks || []}
          onTaskClick={onTaskClick}
        />
      </Box>
    </Box>
  );
};
